import React, { useState, useEffect } from 'react';
import { ChevronDown, FileText, Calendar, User, FolderOpen } from 'lucide-react';
import { MedicalReviewUploadZone } from './MedicalReviewUploadZone';
import { DocumentPreviewPanel } from '../documents/DocumentPreviewPanel';
import { medicalReviewService } from '../../services/medicalReviewService';
import { SharePointEmbeddedService } from '../../services/sharePointEmbeddedService';
import { SPEAuthProvider } from '../../services/speAuthProvider';
import type { Matter } from '../../types';
import type { LegalDocument } from '../../services/legalDocumentService';

interface MedicalReviewDashboardProps {
  matters: Matter[];
  className?: string;
}

interface MedicalDocumentInfo {
  documents: LegalDocument[];
  containerCreated?: boolean;  // Make optional to handle undefined
}

export const MedicalReviewDashboard: React.FC<MedicalReviewDashboardProps> = ({
  matters,
  className = ''
}) => {
  const [selectedMatter, setSelectedMatter] = useState<Matter | null>(null);
  const [showMatterDropdown, setShowMatterDropdown] = useState(false);
  const [medicalDocuments, setMedicalDocuments] = useState<MedicalDocumentInfo>({ documents: [], containerCreated: false });
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  
  // Document preview state
  const [previewDocument, setPreviewDocument] = useState<LegalDocument | null>(null);
  const [isPreviewPanelOpen, setIsPreviewPanelOpen] = useState(false);

  // Auto-select first matter if only one available, or if none selected
  useEffect(() => {
    if (!selectedMatter && matters.length > 0) {
      setSelectedMatter(matters[0]);
    }
  }, [matters, selectedMatter]);

  // Load medical documents when matter is selected
  useEffect(() => {
    if (selectedMatter) {
      loadMedicalDocuments(selectedMatter);
    }
  }, [selectedMatter]);

  const loadMedicalDocuments = async (matter: Matter) => {
    setIsLoadingDocuments(true);
    try {
      const result = await medicalReviewService.getMedicalDocuments(matter);
      setMedicalDocuments({
        documents: result.documents,
        containerCreated: result.containerCreated || false
      });
    } catch (error) {
      console.error('Failed to load medical documents:', error);
      setMedicalDocuments({ documents: [], containerCreated: false });
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  // Handle medical records upload
  const handleMedicalRecordsUpload = async (files: File[]) => {
    if (!selectedMatter) {
      throw new Error('No matter selected');
    }

    try {
      console.log('Starting medical records upload:', files);
      console.log('Uploading to matter:', selectedMatter.title);

      // Upload the files using the existing proven document system
      const uploadResult = await medicalReviewService.uploadDocuments({
        matter: selectedMatter,
        files,
        caseType: 'other' // Could add UI to select case type
      });

      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Failed to upload files');
      }

      console.log('Medical records uploaded successfully:', uploadResult);
      
      // Reload medical documents to show the newly uploaded files
      await loadMedicalDocuments(selectedMatter);
      
    } catch (error: any) {
      console.error('Medical records upload failed:', error);
      throw error; // Re-throw to let the upload zone handle the error display
    }
  };

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  // Handle document view - same pattern as DocumentSection.tsx
  const handleDocumentView = async (doc: LegalDocument) => {
    if (!selectedMatter?.speContainerId) {
      console.error('No SharePoint container ID available for matter');
      return;
    }

    // Check if this is an Office document that should open directly in new tab
    const isOfficeDoc = ['.docx', '.xlsx', '.pptx', '.doc', '.xls', '.ppt'].some(ext => 
      doc.name.toLowerCase().endsWith(ext)
    );
    
    if (isOfficeDoc) {
      try {
        const authProvider = new SPEAuthProvider();
        const speService = new SharePointEmbeddedService(authProvider);
        
        let previewUrl: string;
        try {
          previewUrl = await speService.getDocumentEmbedUrl(selectedMatter.speContainerId, doc.id);
        } catch {
          // Fallback to preview URL
          previewUrl = await speService.getDocumentPreviewUrl(selectedMatter.speContainerId, doc.id);
        }
        
        // Open in new tab immediately
        window.open(previewUrl, '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.error('Failed to open Office document:', error);
        // If direct opening fails, fall back to side panel
        setPreviewDocument(doc);
        setIsPreviewPanelOpen(true);
      }
    } else {
      // For PDFs and other documents, use the side panel
      setPreviewDocument(doc);
      setIsPreviewPanelOpen(true);
    }
  };

  // Close document preview panel
  const closePreviewPanel = () => {
    setIsPreviewPanelOpen(false);
    setPreviewDocument(null);
  };

  const activeMedicalDocuments = medicalDocuments.documents; // All documents are active by default

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-white">Medical Records Review</h2>
        <p className="text-sm text-gray-400 mt-1">
          Upload and manage medical documents for chronological analysis
        </p>
      </div>

      {/* Matter Selection */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-300">Select Matter</label>
        <div className="relative">
          <button
            onClick={() => setShowMatterDropdown(!showMatterDropdown)}
            className="w-full flex items-center justify-between px-4 py-3 text-left bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <User className="w-5 h-5 text-gray-400" />
              <div>
                {selectedMatter ? (
                  <>
                    <div className="text-white font-medium">{selectedMatter.title}</div>
                    <div className="text-gray-400 text-sm">{selectedMatter.description || 'No description'}</div>
                  </>
                ) : (
                  <div className="text-gray-400">Select a matter...</div>
                )}
              </div>
            </div>
            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showMatterDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showMatterDropdown && (
            <div className="absolute z-10 w-full mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-lg max-h-64 overflow-y-auto">
              {matters.length === 0 ? (
                <div className="px-4 py-3 text-gray-400 text-sm">
                  No matters available. Create a matter first.
                </div>
              ) : (
                matters.map((matter) => (
                  <button
                    key={matter.id}
                    onClick={() => {
                      setSelectedMatter(matter);
                      setShowMatterDropdown(false);
                    }}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-600 transition-colors ${
                      selectedMatter?.id === matter.id ? 'bg-gray-600' : ''
                    }`}
                  >
                    <div className="text-white font-medium">{matter.title}</div>
                    <div className="text-gray-400 text-sm">{matter.description || 'No description'}</div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Upload Zone - Always show for selected matter */}
      {selectedMatter && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-white">Upload Medical Records</h3>
            <span className="text-sm text-gray-400">
              Files will be stored in: {selectedMatter.title} → Medical Records
            </span>
          </div>
          
          <MedicalReviewUploadZone 
            onFilesUpload={handleMedicalRecordsUpload}
            className="min-h-0" 
          />
        </div>
      )}

      {/* Medical Documents Display - Show uploaded documents below upload zone */}
      {selectedMatter && (
        <div className="space-y-4">
          {isLoadingDocuments ? (
            <div className="text-center py-8">
              <div className="text-gray-400">Loading medical records...</div>
            </div>
          ) : activeMedicalDocuments.length > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FolderOpen className="w-5 h-5 text-blue-400" />
                  <h3 className="text-lg font-medium text-white">
                    Uploaded Medical Records ({activeMedicalDocuments.length})
                  </h3>
                </div>
                <button
                  onClick={() => loadMedicalDocuments(selectedMatter)}
                  className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Refresh
                </button>
              </div>
              
              <div className="grid gap-3">
                {activeMedicalDocuments.map((document) => (
                  <div
                    key={document.id}
                    className="flex items-center justify-between p-4 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <FileText className="w-5 h-5 text-blue-400" />
                      <div>
                        <div className="text-white font-medium">{document.name}</div>
                        <div className="flex items-center space-x-4 text-sm text-gray-400">
                          <span className="flex items-center space-x-1">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(document.createdDateTime)}</span>
                          </span>
                          <span>{formatFileSize(document.size)}</span>
                          {document.category && (
                            <span className="px-2 py-1 bg-green-900/20 text-green-400 rounded text-xs">
                              Medical Record
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => handleDocumentView(document)}
                      className="px-3 py-1 text-sm text-blue-400 hover:text-blue-300 border border-blue-400 rounded hover:border-blue-300 transition-colors"
                    >
                      View
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-6 border-2 border-dashed border-gray-600 rounded-lg bg-gray-800/50">
              <FolderOpen className="w-8 h-8 text-gray-500 mx-auto mb-3" />
              <h4 className="text-md font-medium text-white mb-2">No Medical Records Yet</h4>
              <p className="text-gray-400 text-sm">
                Upload documents above to see them listed here
              </p>
            </div>
          )}
        </div>
      )}

      {/* No Matter Selected State */}
      {!selectedMatter && matters.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed border-gray-600 rounded-lg">
          <User className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No Matters Available</h3>
          <p className="text-gray-400">
            Create a matter first to upload medical records
          </p>
        </div>
      )}
      
      {/* Document Preview Panel */}
      <DocumentPreviewPanel
        document={previewDocument}
        containerId={selectedMatter?.speContainerId || ''}
        isOpen={isPreviewPanelOpen}
        onClose={closePreviewPanel}
      />
    </div>
  );
};